import { ReactNode, forwardRef } from 'react'
import { RefCallBack } from 'react-hook-form'
import { InputLayout, InputThemeStyle } from 'components/atoms/inputs/InputLayout/InputLayout'
import { Input, InputProps } from 'components/atoms/inputs/Input/Input'

type BaseLabeledInputProps = {
  name: string
  inputClassName?: string

  error?: ReactNode
  dynamicHeight?: boolean
  label?: ReactNode
  note?: ReactNode
  units?: ReactNode
  hasFocus?: boolean
  style?: InputThemeStyle
  horizontal?: boolean
  className?: string
  innerRef?: RefCallBack
}

type LabeledInputProps<OptionValue extends string> = BaseLabeledInputProps & InputProps<OptionValue>

/**
 * Master Component to use for all forms unless some extreme exception.
 * This will take an input field and wrap it in a label,
 * as well as all other deocrations such as hints, units and errors.
 *
 * Use inside react-hook-form context or by nesting inside FormPreset
 *
 * @type - InputType - See the Insight.jsx component to see which options are available
 */
export const LabeledInput = forwardRef(<OptionValue extends string>(
  props: LabeledInputProps<OptionValue>,
  ref: RefCallBack
) => {
  const {
    inputClassName,
    className,
    error,
    dynamicHeight,
    label,
    note,
    units,
    hasFocus,
    style,
    horizontal,
    ...inputProps
  } = props

  const layoutProps = {
    className,
    error,
    dynamicHeight,
    label,
    note,
    units,
    hasFocus,
    horizontal,
  }

  const inputHasOptions = inputProps.type === 'select' || inputProps.type === 'radio' || inputProps.type === 'checkboxes'

  return (
    <InputLayout
      { ...layoutProps }
      wrapInLabel={ !inputHasOptions }
      textInputMode={ !inputHasOptions }
      themeStyle={ style }
    >
      <Input<OptionValue> { ...inputProps } innerRef={ref} className={ inputClassName } />
    </InputLayout>
  )
})

LabeledInput.displayName = 'LabeledInput'
